/**
 * 本地存储的数据字典缓存管理器
 * 将数据字典信息缓存到浏览器本地存储中，提升访问性能
 */

import { DictItem } from '../hooks/useUnifiedDict';

// 本地缓存接口
interface LocalDictCache {
  data: DictItem[];
  timestamp: number;
  version: string;
}

// 缓存配置
const CACHE_CONFIG = {
  // 缓存键前缀
  CACHE_KEY_PREFIX: 'dict_cache_',
  // 缓存过期时间（24小时）
  CACHE_EXPIRE_TIME: 24 * 60 * 60 * 1000,
  // 缓存版本
  CACHE_VERSION: '1.0.0',
  // 最大缓存数量
  MAX_CACHE_SIZE: 100,
};

class LocalDictCacheManager {
  private static instance: LocalDictCacheManager;
  private cache: Map<string, LocalDictCache> = new Map();
  private isInitialized = false;
  private loadingPromises: Map<string, Promise<any>> = new Map();

  /**
   * 获取单例实例
   */
  public static getInstance(): LocalDictCacheManager {
    if (!LocalDictCacheManager.instance) {
      LocalDictCacheManager.instance = new LocalDictCacheManager();
    }
    return LocalDictCacheManager.instance;
  }

  /**
   * 初始化缓存管理器
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // 字典数据接口是公开的，不需要token也能访问（网站端使用）
      // 检查用户是否已登录的逻辑已移除，允许无token访问
      await this.loadFromLocalStorage();
      this.isInitialized = true;
    } catch (error) {
      console.error('本地字典缓存管理器初始化失败:', error);
    }
  }

  /**
   * 从本地存储加载缓存
   */
  private async loadFromLocalStorage(): Promise<void> {
    try {
      const cacheKeys = this.getCacheKeys();

      for (const key of cacheKeys) {
        const cacheData = localStorage.getItem(key);
        if (cacheData) {
          try {
            const parsed: LocalDictCache = JSON.parse(cacheData);
            // 检查缓存是否过期
            if (!this.isExpired(parsed.timestamp)) {
              this.cache.set(this.extractDictType(key), parsed);
            } else {
              // 清除过期缓存
              localStorage.removeItem(key);
            }
          } catch (error) {
            localStorage.removeItem(key);
          }
        }
      }

    } catch (error) {
      console.error('从本地存储加载缓存失败:', error);
    }
  }

  /**
   * 获取所有缓存键
   */
  private getCacheKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_CONFIG.CACHE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    return keys;
  }

  /**
   * 从缓存键中提取字典类型
   */
  private extractDictType(cacheKey: string): string {
    return cacheKey.replace(CACHE_CONFIG.CACHE_KEY_PREFIX, '');
  }

  /**
   * 检查缓存是否过期
   */
  private isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > CACHE_CONFIG.CACHE_EXPIRE_TIME;
  }

  /**
   * 获取字典数据（优先从本地缓存）
   */
  public async getDictData(dictType: string): Promise<DictItem[]> {
    // 检查内存缓存
    const memoryCache = this.cache.get(dictType);
    if (memoryCache && !this.isExpired(memoryCache.timestamp)) {
      return memoryCache.data;
    }

    // 检查本地存储缓存
    const localCache = this.getFromLocalStorage(dictType);
    if (localCache && !this.isExpired(localCache.timestamp)) {
      // 更新内存缓存
      this.cache.set(dictType, localCache);
      return localCache.data;
    }

    // 检查是否正在加载中，避免重复请求
    const existingPromise = this.loadingPromises.get(dictType);
    if (existingPromise) {
      return await existingPromise;
    }

    // 从服务器获取
    const promise = this.fetchFromServer(dictType);
    this.loadingPromises.set(dictType, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      // 请求完成后清除加载状态
      this.loadingPromises.delete(dictType);
    }
  }

  /**
   * 从本地存储获取缓存
   */
  private getFromLocalStorage(dictType: string): LocalDictCache | null {
    try {
      const cacheKey = this.getCacheKey(dictType);
      const cacheData = localStorage.getItem(cacheKey);
      if (cacheData) {
        return JSON.parse(cacheData);
      }
    } catch (error) {
    }
    return null;
  }

  /**
   * 从服务器获取字典数据
   */
  private async fetchFromServer(dictType: string): Promise<DictItem[]> {
    try {
      const { dictDataService } = await import('../services/dictService');

      // 使用新的不分页接口获取完整数据
      const response = await dictDataService.getAllByType(dictType);

      if (response.code === 200) {
        // 直接使用返回的数据，不需要处理分页结构
        const data = (response.data || []).sort((a: any, b: any) => (a.dictSort || 0) - (b.dictSort || 0));

        // 缓存到本地
        await this.setDictData(dictType, data);

        return data;
      } else {
        console.error(`API返回错误:`, response);
        throw new Error(response.message || '获取字典数据失败');
      }
    } catch (error) {
      console.error(`从服务器获取字典数据失败: ${dictType}`, error);
      throw error;
    }
  }

  /**
   * 设置字典数据到缓存
   */
  public async setDictData(dictType: string, data: DictItem[]): Promise<void> {
    const cacheData: LocalDictCache = {
      data,
      timestamp: Date.now(),
      version: CACHE_CONFIG.CACHE_VERSION,
    };

    // 更新内存缓存
    this.cache.set(dictType, cacheData);

    // 更新本地存储
    try {
      const cacheKey = this.getCacheKey(dictType);
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error(`缓存字典数据到本地存储失败: ${dictType}`, error);
    }
  }

  /**
   * 批量预加载字典数据（优化版本：一次请求获取所有数据）
   */
  public async preloadDicts(dictTypes: string[]): Promise<void> {

    // 检查是否已经有批量加载正在进行
    const batchKey = 'batch_preload';
    const existingBatchPromise = this.loadingPromises.get(batchKey);
    if (existingBatchPromise) {
      return await existingBatchPromise;
    }

    const batchPromise = this.doPreloadDicts(dictTypes);
    this.loadingPromises.set(batchKey, batchPromise);

    try {
      await batchPromise;
    } finally {
      this.loadingPromises.delete(batchKey);
    }
  }

  /**
   * 执行批量预加载
   */
  private async doPreloadDicts(dictTypes: string[]): Promise<void> {
    try {
      const { dictDataService } = await import('../services/dictService');

      // 使用批量接口一次获取所有字典数据
      const response = await dictDataService.getBatchByTypes(dictTypes);

      if (response.code === 200) {
        const batchData = response.data;

        // 将批量数据分别缓存到本地
        for (const [dictType, data] of Object.entries(batchData)) {
          await this.setDictData(dictType, data as DictItem[]);
        }

      } else {
        throw new Error(response.message || '批量获取字典数据失败');
      }
    } catch (error) {
      console.error('批量预加载失败，回退到逐个获取:', error);

      // 如果批量获取失败，回退到逐个获取
      const promises = dictTypes.map(async (dictType) => {
        try {
          await this.getDictData(dictType);
        } catch (error) {
          console.error(`逐个预加载失败: ${dictType}`, error);
        }
      });

      await Promise.all(promises);
    }
  }

  /**
   * 清除指定字典类型的缓存
   */
  public clearDictCache(dictType?: string): void {
    if (dictType) {
      // 清除指定字典类型的缓存
      this.cache.delete(dictType);
      const cacheKey = this.getCacheKey(dictType);
      localStorage.removeItem(cacheKey);
    } else {
      // 清除所有字典缓存
      this.cache.clear();
      const cacheKeys = this.getCacheKeys();
      cacheKeys.forEach(key => localStorage.removeItem(key));
    }
  }

  /**
   * 清除过期缓存
   */
  public clearExpiredCache(): void {
    const now = Date.now();
    let clearedCount = 0;

    // 清除内存中的过期缓存
    for (const [dictType, cache] of Array.from(this.cache.entries())) {
      if (this.isExpired(cache.timestamp)) {
        this.cache.delete(dictType);
        clearedCount++;
      }
    }

    // 清除本地存储中的过期缓存
    const cacheKeys = this.getCacheKeys();
    cacheKeys.forEach(key => {
      try {
        const cacheData = localStorage.getItem(key);
        if (cacheData) {
          const parsed: LocalDictCache = JSON.parse(cacheData);
          if (this.isExpired(parsed.timestamp)) {
            localStorage.removeItem(key);
            clearedCount++;
          }
        }
      } catch (error) {
        // 解析失败，直接删除
        localStorage.removeItem(key);
        clearedCount++;
      }
    });

  }

  /**
   * 获取缓存状态信息
   */
  public getCacheStatus(): {
    memoryCacheCount: number;
    localStorageCacheCount: number;
    totalSize: number;
    cacheTypes: string[];
  } {
    const cacheKeys = this.getCacheKeys();
    let totalSize = 0;

    cacheKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        totalSize += data.length;
      }
    });

    return {
      memoryCacheCount: this.cache.size,
      localStorageCacheCount: cacheKeys.length,
      totalSize,
      cacheTypes: Array.from(this.cache.keys()),
    };
  }

  /**
   * 检查字典数据是否需要更新
   */
  public async checkForUpdates(dictType: string): Promise<boolean> {
    try {
      const { dictDataService } = await import('../services/dictService');

      // 使用新的不分页接口获取完整数据来检查更新
      const response = await dictDataService.getAllByType(dictType);

      if (response.code === 200) {
        const serverCount = response.data ? response.data.length : 0;
        const localCache = this.cache.get(dictType);
        const localCount = localCache ? localCache.data.length : 0;

        return serverCount !== localCount;
      }
    } catch (error) {
      console.error(`检查字典更新失败: ${dictType}`, error);
    }

    return false;
  }

  /**
   * 获取缓存键
   */
  private getCacheKey(dictType: string): string {
    return `${CACHE_CONFIG.CACHE_KEY_PREFIX}${dictType}`;
  }

  /**
   * 导出缓存数据（用于调试）
   */
  public exportCache(): string {
    const cacheData: { [key: string]: LocalDictCache } = {};
    for (const [dictType, cache] of Array.from(this.cache.entries())) {
      cacheData[dictType] = cache;
    }
    return JSON.stringify(cacheData, null, 2);
  }

  /**
   * 导入缓存数据（用于调试）
   */
  public importCache(cacheJson: string): void {
    try {
      const cacheData = JSON.parse(cacheJson);
      for (const [dictType, cache] of Object.entries(cacheData)) {
        this.cache.set(dictType, cache as LocalDictCache);
        const cacheKey = this.getCacheKey(dictType);
        localStorage.setItem(cacheKey, JSON.stringify(cache));
      }
    } catch (error) {
      console.error('缓存数据导入失败:', error);
    }
  }
}

// 创建全局实例
export const localDictCacheManager = LocalDictCacheManager.getInstance();

export default localDictCacheManager;